import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "../utils/toast-notification";
import { useAuth } from "../context/auth-context";
import { getUser, isSuperadmin } from "../auth-provider";
import ApiClient from "./../utils/api-client";
import { Link, useHistory } from "react-router-dom";
import { apiErrorMessage } from "../utils/api-error";
import { Logo } from "../components/Logo";

type LoginView = "sign-in" | "reset";

const inputClass =
  "appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5";

const Login = () => {
  const { t } = useTranslation();
  const { loginUser } = useAuth();
  const history = useHistory();
  const [view, setView] = useState<LoginView>("sign-in");
  const [email, setEmail] = useState("");
  const [resetNotice, setResetNotice] = useState("");
  const [sendingReset, setSendingReset] = useState(false);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    // to type the target correctly
    const target = e.target as typeof e.target & {
      email: { value: string };
      password: { value: string };
    };
    const password = target.password.value;
    try {
      const res = await ApiClient.login(email, password);
      await loginUser(res?.data.token);
      const user = await getUser();
      history.push(isSuperadmin(user) ? "/users-management/" : "/dashboard");
    } catch (error) {
      toast.error(apiErrorMessage(error, "could not log you in"));
    }
  };

  const handleReset = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setSendingReset(true);
    try {
      // forgotPassword resolves with the axios error instead of rejecting.
      const res = await ApiClient.forgotPassword(email);
      if (isPasswordResetError(res)) {
        toast.error(passwordResetErrorMessage(res) || t("login.reset-failed"));
        return;
      }
      const message = passwordResetSuccess(res) || t("login.reset-sent");
      setResetNotice(message);
      toast.success(message);
    } finally {
      setSendingReset(false);
    }
  };

  const showSignIn = () => {
    setView("sign-in");
    setResetNotice("");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Logo className="max-w-full" />
        <h2 className="mt-6 text-center text-3xl leading-9 font-extrabold text-gray-900">
          {t(loginHeadingKey(view))}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {view === "sign-in" ? (
            <form onSubmit={handleSubmit}>
              <EmailField email={email} onEmail={setEmail} label={t("login.email")} />

              <div className="mt-6">
                <div className="flex items-baseline justify-between">
                  <label
                    htmlFor="password"
                    className="text-sm font-medium leading-5 text-gray-700"
                  >
                    {t("login.password")}
                  </label>
                  <button
                    type="button"
                    onClick={() => setView("reset")}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500 whitespace-nowrap"
                  >
                    {t("login.forgot")}
                  </button>
                </div>
                <div className="mt-1 rounded-md shadow-sm">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className={inputClass}
                  />
                </div>
              </div>
              <div className="mt-6">
                <span className="block w-full rounded-md shadow-sm">
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:ring-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                  >
                    {t("login.signin")}
                  </button>
                </span>
              </div>
            </form>
          ) : resetNotice ? (
            <p className="text-sm text-gray-700" role="status">
              {resetNotice}
            </p>
          ) : (
            <form onSubmit={handleReset}>
              <EmailField email={email} onEmail={setEmail} label={t("login.email")} />
              <div className="mt-6">
                <span className="block w-full rounded-md shadow-sm">
                  <button
                    type="submit"
                    disabled={sendingReset}
                    className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:ring-indigo active:bg-indigo-700 transition duration-150 ease-in-out disabled:opacity-50"
                  >
                    {t("login.reset-submit")}
                  </button>
                </span>
              </div>
            </form>
          )}

          {view === "reset" ? (
            <p className="mt-6 text-center text-sm">
              <button
                type="button"
                onClick={showSignIn}
                className="text-indigo-600 hover:text-indigo-500"
              >
                {t("register.back-to-login")}
              </button>
            </p>
          ) : null}

          <p className="mt-6 text-center text-sm">
            <Link to="/register" className="text-indigo-600 hover:text-indigo-500">
              {t("register.link")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const EmailField = ({
  email,
  onEmail,
  label,
}: {
  email: string;
  onEmail: (value: string) => void;
  label: string;
}) => (
  <div>
    <label htmlFor="email" className="block text-sm font-medium leading-5 text-gray-700">
      {label}
    </label>
    <div className="mt-1 rounded-md shadow-sm">
      <input
        id="email"
        name="email"
        type="email"
        required
        value={email}
        onChange={(event) => onEmail(event.target.value)}
        className={inputClass}
      />
    </div>
  </div>
);

function loginHeadingKey(view: LoginView): string {
  switch (view) {
    case "sign-in":
      return "login.signin-header";
    case "reset":
      return "login.reset-header";
    default: {
      const unreachable: never = view;
      return unreachable;
    }
  }
}

function isPasswordResetError(res: unknown): boolean {
  const body = res as { isAxiosError?: boolean; response?: unknown };
  return Boolean(body && (body.isAxiosError || body.response));
}

function passwordResetErrorMessage(res: unknown): string {
  const message = (res as { response?: { data?: { message?: string } } })?.response?.data
    ?.message;
  return typeof message === "string" ? message : "";
}

function passwordResetSuccess(res: unknown): string {
  const message = (res as { message?: string })?.message;
  return typeof message === "string" ? message : "";
}

export default Login;
